import { useRouter } from "next/router";
import {
  RefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useController, UseFormReturn, FieldErrors } from "react-hook-form";
import { useTranslation } from "@/hooks/useTranslation";
import {
  useMutation,
  UseQueryResult,
  useIsMutating,
} from "@tanstack/react-query";
import { Path } from "react-hook-form";

import { PaymentBehavior } from "@/networking/payments.api";
import { CountriesAndLanguages } from "@/networking/addresses.api";
import {
  CartResponse,
  updateCartAttributes,
  updateCartMetaData,
} from "@/networking/carts.api";
import { checkoutCart } from "@/networking/checkout.api";
import {
  handleVgsCollectTokenization,
  VgsCollectSubmitResponse,
} from "@/networking/vault.api";
import { ShippingMethod } from "@/networking/shipping.api";
import { createAddressCountryReset } from "@/lib/address-country-selection";
import { flattenPerItemErrors } from "@/lib/flatten-per-item-errors";
import { pointsLabel } from "@/lib/points-label";

import AddressForm from "@/components/checkout/address-form";
import Agreements from "@/components/checkout/agreements";
import OptionalAgreements from "@/components/checkout/optional-agreements";
import type {
  CheckoutFormData,
  ContactErrorsState,
} from "@/components/checkout/checkout.types";
import ContactInfoForm from "@/components/checkout/contact-info-form";
import PaymentMethodSelect from "@/components/checkout/payment-method-select";
import ShippingMethodSelect from "@/components/checkout/shipping-method-select";
import Footer from "@/components/checkout/footer";
import { Button } from "../ui/button";
import usePaypal from "@/components/checkout/hooks/usePaypal";
import useCartCountry from "@/components/checkout/hooks/useCartCountry";
import useAutocomplete from "@/components/checkout/hooks/useAutocomplete";
import {
  cn,
  getPaymentBehavior,
  hasCreditCardErrors,
  getConsolidatedErrorMessage,
  splitName,
} from "@/lib/utils";
import { isApiError } from "@/lib/api-helpers";
import { checkoutRedirect } from "@/lib/checkout-redirect";
import { sanitizeErrorMessage } from "@/lib/error-sanitizer";
import { useCartMutations } from "@/components/checkout/hooks/useCartMutations";
import FastCheckoutOptions from "./fast-checkout-options";
import { InfoAlert, MutedAlert, WarningAlert } from "@/components/ui/alert";
import { useAgreements } from "./hooks/useAgreements";
import { useHandleCheckout } from "./hooks/useHandleCheckout";
import { useVgs } from "@/hooks/useVgs";
import { hasCartErrors, hasCartItemErrors } from "@/lib/api-helpers";
import { ErrorAlert } from "@/components/ui/alert";
import { usePaymentMethodHandler } from "@/hooks/usePaymentMethodHandler";
import DropZone from "@/components/drop-zones/drop-zone";
import useAddressSync from "./hooks/useAddressSync";
import { useLoadingOverlay } from "../providers/CheckoutLoading";
import { getBrowserInfo } from "@/lib/browser-info";
import { usesVgsCollect } from "@/lib/vgs-utils";
import { cancelVgs3DS, initializeVgs3DS } from "@/networking/three-ds/vgs.api";
import VgsThreeDSModal from "./threeds-modal/vgs";
import BlikOtpModal from "./blik-otp-modal";
import { useVgs3DSCompletion } from "./hooks/useVgs3DSCompletion";
import { useVgsPayment } from "@/hooks/useVgsPayment";
import { usePaymentMethodScript } from "@/hooks/usePaymentMethodScript";
import { PaymentMethodData } from "@/networking/checkout.api";
import MarketingCheckboxes from "./marketing-checkboxes";
import useBraintreePayment from "./hooks/useBraintreePayment";
import { useBraintree } from "@/components/checkout/braintree-provider";

const renderFingerprintingIframe = (html: string): Promise<void> => {
  return new Promise((resolve) => {
    const iframe = document.createElement("iframe");
    iframe.srcdoc = html;
    iframe.style.display = "none";

    iframe.onload = () => {};

    document.body.appendChild(iframe);

    setTimeout(() => {
      document.body.removeChild(iframe);
      resolve();
    }, 10000);
  });
};

export default function GuestCheckout({
  checkoutForm,
  cartToken,
  query,
  countries,
  affirmPublicKey,
  paymentMethodsIncludes,
  scrollRef,
  kountSessionId,
  sdkType,
  braintreePaymentAccountId: _braintreePaymentAccountId,
}: {
  checkoutForm: UseFormReturn<CheckoutFormData>;
  cartToken: string;
  query: UseQueryResult<CartResponse | null>;
  countries: CountriesAndLanguages["countries"];
  affirmPublicKey: string | undefined;
  paymentMethodsIncludes: string | null;
  scrollRef?: React.RefObject<HTMLDivElement | null>;
  kountSessionId?: string;
  sdkType?: string;
  braintreePaymentAccountId?: string;
}) {
  const { t, language } = useTranslation();
  const router = useRouter();
  const { showCheckoutLoader, hideCheckoutLoader } = useLoadingOverlay();
  const isSubscriptionPending =
    useIsMutating({
      mutationKey: ["update-item-subscription", cartToken],
    }) > 0;
  const isQuantityPending =
    useIsMutating({
      mutationKey: ["update-item-quantity", cartToken],
    }) > 0;
  const isDiscountPending =
    useIsMutating({
      mutationKey: ["apply-discount", cartToken],
    }) > 0;
  const isRemoveDiscountPending =
    useIsMutating({
      mutationKey: ["remove-discount", cartToken],
    }) > 0;
  const [paymentLoading, setPaymentLoading] = useState(false);
  const contactInfoRef = useRef<HTMLDivElement>(null);
  const shippingAddressRef = useRef<HTMLDivElement>(null);
  const { subdomain } = query?.data?.cart?.company || {};
  const [contactErrors, setContactErrors] = useState<ContactErrorsState>({});

  const [checkoutError, setCheckoutError] = useState<string | undefined>(
    undefined,
  );
  const [paymentError, setPaymentError] = useState<string | undefined>(
    undefined,
  );
  const [customerDetails, setCustomerDetails] = useState<Record<
    string,
    string
  > | null>(null);
  const [customerDetailsValidationErrors, setCustomerDetailsValidationErrors] =
    useState<Record<string, string[]>>({});
  const [addressError, setAddressError] = useState<string | undefined>(
    undefined,
  );
  const [shippingMethodError, setShippingMethodError] = useState<
    string | undefined
  >(undefined);
  const [emailMarketing, setEmailMarketing] = useState(true);
  const [smsMarketing, setSmsMarketing] = useState(true);

  const updateCartMetaDataMutation = useMutation({
    mutationFn: updateCartMetaData,
  });

  // VGS 3DS modal state
  const [vgsThreeDSModal, setVgsThreeDSModal] = useState<{
    isOpen: boolean;
    challengeHtml: string;
    challengeUrl: string;
    transactionId: string;
    merchantTxId: string;
  }>({
    isOpen: false,
    challengeHtml: "",
    challengeUrl: "",
    transactionId: "",
    merchantTxId: "",
  });

  // BLIK OTP modal state
  const [blikOtpModal, setBlikOtpModal] = useState<{ isOpen: boolean }>({
    isOpen: false,
  });

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    setError,
    clearErrors,
    trigger,
    getValues,
    formState: { errors },
  } = checkoutForm;

  const paymentMethod = watch("customer.payment_method.type");

  const {
    requiredAgreements,
    optionalCheckboxAgreements,
    footerAgreements,
    setOptionalCheckedIds,
    getAcceptedAgreementIds,
  } = useAgreements(query.data?.cart?.agreements || []);
  // Memoize so handleBraintreeSuccess (which closes over this in its
  // useCallback deps) only rebuilds when the underlying IDs change.
  const acceptedAgreementIds = useMemo(
    () => getAcceptedAgreementIds(),
    [getAcceptedAgreementIds],
  );
  // Drives the cart-metadata write for accepted_agreement_ids. Write
  // whenever the cart has any checkout agreements (not just when the
  // user selected something) so an empty current selection explicitly
  // overwrites any stale value left by a prior submission.
  const hasCheckoutAgreements = (query.data?.cart?.agreements?.length ?? 0) > 0;

  const { getPaymentMethodData } = usePaymentMethodScript(
    query.data?.cart?.available_payment_methods,
    paymentMethod,
  );

  useEffect(() => {
    setCustomerDetails(null);
    setCustomerDetailsValidationErrors({});
    setPaymentError(undefined);
  }, [paymentMethod]);

  // clear local state errors on language change
  useEffect(() => {
    setContactErrors({});
    setCheckoutError(undefined);
    setPaymentError(undefined);
    setAddressError(undefined);
    setShippingMethodError(undefined);
    setCustomerDetailsValidationErrors({});
  }, [language]);

  const formEmail = watch("customer.contact_info.email");

  const handlePaypalSuccess = useCallback(async () => {
    // Optional: any success handling later
  }, []);

  const handlePaypalError = useCallback((error: Error) => {
    console.error("PayPal payment failed:", error);
    setPaymentError(sanitizeErrorMessage(error.message));
  }, []);

  const { paypalButton, venmoButton, payLaterButton } = usePaypal({
    fastCheckout: false,
    cartToken,
    onSuccess: handlePaypalSuccess,
    onError: handlePaypalError,
    paymentMethodsIncludes,
  });

  const handleBraintreeSuccess = useCallback(
    async (_result: { transaction_id: string }) => {
      try {
        showCheckoutLoader("Placing your order...");

        // Persist accepted_agreement_ids before the first checkout call
        // — the Braintree PayPal/Venmo path bypasses the regular submit
        // handler (its own button replaces the submit button), so
        // without this the metadata write that the submit flow does
        // never happens and the pre-purchase finalize would lose the
        // optional-checked IDs.
        if (hasCheckoutAgreements) {
          await updateCartMetaDataMutation.mutateAsync({
            cartToken,
            metaData: { accepted_agreement_ids: acceptedAgreementIds },
          });
        }

        const checkoutResult = await checkoutCart(cartToken, {
          acceptedAgreementIds,
        });
        checkoutRedirect(checkoutResult, cartToken);
      } catch (error) {
        hideCheckoutLoader();
        console.error("Braintree checkout error:", error);
        setPaymentError(
          sanitizeErrorMessage(
            error instanceof Error ? error.message : undefined,
            "Checkout failed. Please try again.",
          ),
        );
      }
    },
    [
      cartToken,
      showCheckoutLoader,
      hideCheckoutLoader,
      acceptedAgreementIds,
      hasCheckoutAgreements,
      updateCartMetaDataMutation,
    ],
  );

  const handleBraintreeError = useCallback((error: string) => {
    setPaymentError(sanitizeErrorMessage(error));
  }, []);

  const { deviceData } = useBraintree();

  const {
    paypalButton: braintreePaypalBtn,
    venmoButton: braintreeVenmoBtn,
    payLaterButton: braintreePayLaterBtn,
    payLaterMessage: braintreePayLaterMsg,
  } = useBraintreePayment({
    cartToken,
    onSuccess: handleBraintreeSuccess,
    onError: handleBraintreeError,
    paymentMethodsIncludes,
  });

  // Address management hooks
  const isDigitalOnly = query?.data?.cart?.digital_only ?? false;
  const fieldPath = isDigitalOnly
    ? "customer.payment_method.billing_address"
    : "customer.shipping_address";
  const {
    handleCountryChange,
    stateOptions,
    addressFields: shippingAddressFieldsConfig,
  } = useCartCountry(
    cartToken,
    paymentMethodsIncludes,
    query?.data?.cart?.country?.iso,
    countries,
  );
  const {
    renderAutocompleteOptions,
    getAutocompleteOptions,
    handleOptionClick,
  } = useAutocomplete(
    setValue,
    `${fieldPath}.city` as Path<CheckoutFormData>,
    `${fieldPath}.state` as Path<CheckoutFormData>,
    `${fieldPath}.postal_code` as Path<CheckoutFormData>,
    `${fieldPath}.address1` as Path<CheckoutFormData>,
    stateOptions,
  );

  // Handler for country change with field reset
  const onShippingCountryChange = useCallback(
    (countryCode: string, countryName: string) => {
      const resetData = createAddressCountryReset(countryCode, countryName);

      Object.entries(resetData).forEach(([key, value]) => {
        setValue(
          `${fieldPath}.${key}` as Path<CheckoutFormData>,
          value as string,
          { shouldValidate: false },
        );
      });

      handleCountryChange(countryCode, !isDigitalOnly);
    },
    [setValue, handleCountryChange, fieldPath, isDigitalOnly],
  );

  const paymentMethodAutoAssignable =
    query.data?.cart?.payment_method_auto_assignable ?? false;

  const requiresPaymentMethod =
    query.data?.cart?.requires_payment_method ?? true;

  const { getPaymentAccountId } = useVgs(query);

  const { updateAddressMutation, updateShippingMethodMutation } =
    useCartMutations(cartToken, paymentMethodsIncludes);

  const { field: addressFields } = useController({
    name: "customer.shipping_address",
    control,
  });
  const { addressSyncPending } = useAddressSync(
    addressFields,
    cartToken,
    paymentMethodsIncludes,
    watch,
    "ship_to",
    setError,
    clearErrors,
  );

  const { field: billingAddressFields } = useController({
    name: "customer.payment_method.billing_address",
    control,
  });
  const { addressSyncPending: billingAddressSyncPending } = useAddressSync(
    billingAddressFields,
    cartToken,
    paymentMethodsIncludes,
    watch,
    "bill_to",
    setError,
    clearErrors,
  );

  const { handlePaymentMethod, isPaymentLoading } =
    usePaymentMethodHandler(cartToken);

  const checkoutMutation = useMutation({
    mutationFn: ({
      cartToken,
      shouldIncludeBrowserInfo,
      kountSessionId,
      paymentMethodData,
      deviceData,
      acceptedAgreementIds,
    }: {
      cartToken: string;
      shouldIncludeBrowserInfo: boolean;
      // Marks the post-3DS retry from handleVgs3DSRedirectSuccess so
      // onError can reroute field errors to the checkout banner.
      isPostThreeDsRetry?: boolean;
      kountSessionId?: string;
      paymentMethodData?: PaymentMethodData;
      deviceData?: string;
      acceptedAgreementIds?: number[];
    }) => {
      const browserInfo = shouldIncludeBrowserInfo
        ? getBrowserInfo()
        : undefined;
      return checkoutCart(cartToken, {
        browserInfo,
        kountSessionId,
        paymentMethodData,
        deviceData,
        acceptedAgreementIds,
      });
    },
    onError: (error: unknown, variables) => {
      // Explicit marker set only by handleVgs3DSRedirectSuccess — decoupled
      // from shouldIncludeBrowserInfo so a future caller passing
      // shouldIncludeBrowserInfo: false for a non-3DS reason doesn't get its
      // errors rerouted to the checkout banner.
      const isPostThreeDsRetry = variables.isPostThreeDsRetry === true;
      hideCheckoutLoader();

      if (isApiError(error)) {
        // Pre-clear field errors so a stale prior error doesn't render alongside
        // whatever the switch below sets (mirrors logged-in-checkout's pattern).
        setContactErrors({});
        setAddressError(undefined);
        setShippingMethodError(undefined);
        setPaymentError(undefined);
        setCheckoutError(undefined);

        const errorsData = error?.data?.errors;

        // handle errors array with JSON strings (e.g. bill_to validation)
        if (Array.isArray(errorsData)) {
          const parsedErrors: string[] = [];
          errorsData.forEach((errStr) => {
            if (typeof errStr === "string") {
              try {
                const parsed = JSON.parse(errStr);
                Object.entries(parsed).forEach(([_section, fields]) => {
                  if (fields && typeof fields === "object") {
                    Object.entries(fields as Record<string, string[]>).forEach(
                      ([field, messages]) => {
                        if (Array.isArray(messages)) {
                          const fieldName = field
                            .replace(/_/g, " ")
                            .replace(/\b\w/g, (c) => c.toUpperCase());
                          parsedErrors.push(
                            `${fieldName} ${messages.join(", ")}`,
                          );
                        }
                      },
                    );
                  }
                });
              } catch {
                parsedErrors.push(errStr);
              }
            }
          });

          if (parsedErrors.length > 0) {
            setCheckoutError(parsedErrors.join(". "));
            return;
          }
        }

        // handle errors as object with attribute keys
        if (
          errorsData &&
          typeof errorsData === "object" &&
          !Array.isArray(errorsData)
        ) {
          for (const [attribute, messages] of Object.entries(errorsData)) {
            if (
              attribute &&
              messages &&
              Array.isArray(messages) &&
              messages.length > 0
            ) {
              const errorMessage = messages[0];
              switch (attribute) {
                case "payment":
                case "payment_method":
                case "payment_account":
                case "vgs":
                case "three_ds":
                  setPaymentError(sanitizeErrorMessage(errorMessage));
                  break;
                case "address":
                case "shipping_address":
                  if (isPostThreeDsRetry) {
                    setCheckoutError(t("payment_verification_didnt_complete"));
                  } else {
                    setAddressError(errorMessage);
                  }
                  break;
                case "email":
                  // Unchanged: preserve guest's multi-step contact UX.
                  setContactErrors((prev) => ({
                    ...prev,
                    email: errorMessage,
                  }));
                  setError("customer.contact_info.email", { type: "manual" });
                  contactInfoRef.current?.scrollIntoView({
                    behavior: "smooth",
                  });
                  break;
                case "shipping_method":
                  setShippingMethodError(errorMessage);
                  break;
                case "items": {
                  // Per-item error rows from the checkout orchestrator
                  // (e.g. `[{ id, customer_limit: ["..."] }]`) — flatten the
                  // inner string arrays instead of String()-ing objects
                  // (mirrors logged-in-checkout's CURRENT-1487 handling).
                  const flattened = flattenPerItemErrors(messages);
                  if (flattened.length > 0) {
                    setCheckoutError(flattened.join(". "));
                  }
                  break;
                }
                case "bundle_inventory":
                case "bundle_child_items":
                case "bundle_configuration":
                case "bundle_customer_limit":
                case "country":
                case "cart":
                  setCheckoutError(errorMessage);
                  break;
                default:
                  setCheckoutError(
                    sanitizeErrorMessage(
                      errorMessage,
                      t("unable_to_process_checkout"),
                    ),
                  );
                  break;
              }
            }
          }
        } else if (error?.data?.error_message) {
          setCheckoutError(
            sanitizeErrorMessage(
              error.data.error_message,
              t("unable_to_process_checkout"),
            ),
          );
        } else {
          setCheckoutError(t("unable_to_process_checkout"));
        }
      } else {
        console.error("Unexpected error structure:", error);
        setCheckoutError(t("unable_to_process_checkout"));
      }
    },
  });

  const addEmailMutation = useMutation({
    mutationFn: updateCartAttributes,
    onError: () => {
      setContactErrors((prev) => ({ ...prev, email: t("invalid_email") }));
    },
  });

  const onSelectShippingMethod = (shippingMethod: ShippingMethod) => {
    updateShippingMethodMutation.mutate({
      shippingMethodId: shippingMethod.id,
    });
  };

  const handleCheckout = useHandleCheckout(
    cartToken,
    checkoutMutation,
    setPaymentLoading,
    setVgsThreeDSModal,
    kountSessionId,
    getPaymentMethodData,
    setBlikOtpModal,
    deviceData ?? undefined,
    acceptedAgreementIds,
  );

  const handleVgsCollectSubmitRef = useRef<
    ((vgsResponse: VgsCollectSubmitResponse) => Promise<void>) | null
  >(null);

  const handleVgsSuccess = useCallback((response: VgsCollectSubmitResponse) => {
    handleVgsCollectSubmitRef.current?.(response);
  }, []);

  const handleVgsError = useCallback(
    (error: string) => {
      setPaymentError(error);
      setPaymentLoading(false);
      hideCheckoutLoader();
    },
    [hideCheckoutLoader],
  );

  const {
    hasActiveSession,
    isFormReady,
    iframeLoadError,
    submitCard,
    vgsFieldState,
    vaultCredentials,
    resetVgs,
  } = useVgsPayment({
    cartToken,
    selectedPaymentType: paymentMethod,
    paymentOptions: query.data?.cart?.available_payment_methods || [],
    onSuccess: handleVgsSuccess,
    onError: handleVgsError,
  });

  useEffect(() => {
    if (isFormReady) {
      setPaymentError(undefined);
    }
  }, [isFormReady]);

  const handleVgsCollectSubmit = useCallback(
    async (vgsResponse: VgsCollectSubmitResponse) => {
      try {
        setPaymentLoading(true);

        const expValue = vgsResponse?.card_exp;

        if (!expValue) {
          throw new Error("Missing expiration date");
        }

        const cleanExp = expValue.replace(/\s+/g, "");
        const [expMonth, expYearShort] = cleanExp.split("/");

        if (!expMonth || !expYearShort) {
          throw new Error("Invalid expiration date format");
        }

        const expYear =
          expYearShort.length === 2 ? `20${expYearShort}` : expYearShort;

        const billingPostalCode =
          watch("customer.payment_method.billing_address.postal_code") ||
          watch("customer.shipping_address.postal_code") ||
          "";

        const cardHolder = vgsResponse?.card_holder;
        if (!cardHolder?.trim()) {
          setPaymentError(t("cardholder_name_is_required"));
          setPaymentLoading(false);
          hideCheckoutLoader();
          return;
        }

        const paymentAccountId = getPaymentAccountId;

        const creditCardResponse = await handleVgsCollectTokenization({
          cartToken,
          paymentAccountId,
          billing_postal_code: billingPostalCode,
          submitResponse: vgsResponse,
          card_holder: cardHolder,
          exp_month: expMonth,
          exp_year: expYear,
        });

        const requires3DS = vaultCredentials?.vault?.requires_3ds ?? false;
        const vgsCardId = creditCardResponse.card?.vgs_card_id;

        if (requires3DS && vgsCardId) {
          showCheckoutLoader("Initializing secure verification...");

          const initResult = await initializeVgs3DS({
            cartToken,
            vgsCardId,
          });

          if (
            initResult.three_ds_required === false ||
            initResult.fingerprinting_required === false
          ) {
            await handleCheckout();
            return;
          }

          if (initResult.tds_method_content) {
            await renderFingerprintingIframe(initResult.tds_method_content);
          }

          hideCheckoutLoader();
        }

        await handleCheckout();
      } catch (error) {
        console.error("VGS submission error:", error);

        hideCheckoutLoader();
        setPaymentLoading(false);

        if (isApiError(error)) {
          setPaymentError(
            sanitizeErrorMessage(
              error?.data?.errors?.payment?.[0] || error?.data?.error_message,
            ),
          );
        } else {
          setPaymentError(
            sanitizeErrorMessage(
              error instanceof Error ? error.message : undefined,
            ),
          );
        }
      }
    },
    [
      watch,
      getPaymentAccountId,
      cartToken,
      vaultCredentials,
      handleCheckout,
      showCheckoutLoader,
      hideCheckoutLoader,
      isDigitalOnly,
    ],
  );

  handleVgsCollectSubmitRef.current = handleVgsCollectSubmit;

  const handleValidSubmit = async (data: CheckoutFormData) => {
    let isVgsSubmit = false;
    try {
      setPaymentError(undefined);

      await Promise.all(
        [
          query.data?.cart?.email === null &&
            addEmailMutation.mutateAsync({
              cartToken,
              email: data.customer.contact_info.email,
            }),
          isDigitalOnly
            ? !query.data?.cart?.bill_to &&
              updateAddressMutation.mutateAsync({
                type: "bill_to",
                address: {
                  name:
                    data.customer.payment_method.billing_address?.name ?? "",
                  address1:
                    data.customer.payment_method.billing_address?.address1 ??
                    "",
                  address2:
                    data.customer.payment_method.billing_address?.address2 ??
                    "",
                  city:
                    data.customer.payment_method.billing_address?.city ?? "",
                  state:
                    data.customer.payment_method.billing_address?.state ?? "",
                  postal_code:
                    data.customer.payment_method.billing_address?.postal_code ??
                    "",
                  phone: (
                    data.customer.payment_method.billing_address as
                      | { phone?: string }
                      | undefined
                  )?.phone,
                  country_code:
                    data.customer.payment_method.billing_address
                      ?.country_code ?? "",
                },
                use_shipping_address_for_billing: false,
              })
            : (!query.data?.cart?.ship_to ||
                data.customer.payment_method
                  ?.use_shipping_address_for_billing) &&
              updateAddressMutation.mutateAsync({
                type: data.customer.payment_method
                  ?.use_shipping_address_for_billing
                  ? "both"
                  : "ship_to",
                address: {
                  name: data.customer.shipping_address.name,
                  address1: data.customer.shipping_address.address1,
                  address2: data.customer.shipping_address.address2,
                  city: data.customer.shipping_address.city,
                  state: data.customer.shipping_address.state,
                  postal_code: data.customer.shipping_address.postal_code,
                  phone: (data.customer.shipping_address as { phone?: string })
                    .phone,
                  country_code: data.customer.shipping_address.country_code,
                },
                use_shipping_address_for_billing:
                  data.customer.payment_method
                    ?.use_shipping_address_for_billing ?? false,
              }),
          (() => {
            const metaData: Record<string, unknown> = {};
            if (query.data?.cart?.company?.collect_email_marketing) {
              metaData.email_marketing = emailMarketing;
            }
            if (query.data?.cart?.company?.collect_sms_marketing) {
              metaData.sms_marketing = smsMarketing;
            }
            // Persist accepted agreement IDs (required + optional-checked)
            // on the cart so a second checkoutCart call from
            // PrePurchaseEnrollment can read them back. Without this,
            // the optional selections made here are lost between the
            // two-step finalize flow because they live in this
            // component's useState and don't survive navigation to the
            // enrollment form. Write whenever the cart has any
            // agreements at checkout so an empty current selection
            // explicitly overwrites any stale value (e.g. user
            // unchecked an optional they had ticked on a prior attempt).
            if (hasCheckoutAgreements) {
              metaData.accepted_agreement_ids = acceptedAgreementIds;
            }
            return Object.keys(metaData).length > 0
              ? updateCartMetaDataMutation.mutateAsync({ cartToken, metaData })
              : undefined;
          })(),
        ].filter(Boolean),
      );

      if (paymentMethodAutoAssignable || !requiresPaymentMethod) {
        await handleCheckout();
        return;
      }

      const paymentMethodType = data.customer.payment_method?.type;
      if (!paymentMethodType) {
        setPaymentError("Please select a payment method");
        setPaymentLoading(false);
        return;
      }

      if (
        getPaymentBehavior(paymentMethodType) === PaymentBehavior.CREDIT_CARD
      ) {
        const selectedPaymentMethod =
          query.data?.cart?.available_payment_methods?.find(
            (m) => m.type === paymentMethodType,
          );

        if (usesVgsCollect(selectedPaymentMethod)) {
          // Check VGS fields emptiness
          const allFieldsEmpty = Object.values(vgsFieldState).every(
            (field) => field.isEmpty,
          );

          if (allFieldsEmpty) {
            setPaymentLoading(false);
            setPaymentError("Please enter your card details");
            return;
          }

          isVgsSubmit = true;
          await submitCard();
          return;
        }
        return;
      }

      // get selected payment method for billing address requirement check
      const selectedPaymentMethod =
        query.data?.cart?.available_payment_methods?.find(
          (m) => m.type === paymentMethodType,
        );

      // validate billing address if required
      if (
        selectedPaymentMethod?.collect_billing_address &&
        !data.customer.payment_method?.use_shipping_address_for_billing
      ) {
        const billingValid = await trigger(
          "customer.payment_method.billing_address",
        );
        if (!billingValid) {
          setPaymentLoading(false);
          return;
        }
      }

      await handlePaymentMethod({
        paymentType: paymentMethodType,
        onSuccess: handleCheckout,
        setPaymentError,
        customer_details: customerDetails || undefined,
        onCustomerDetailsValidationErrors: setCustomerDetailsValidationErrors,
        collectBillingAddress:
          selectedPaymentMethod?.collect_billing_address ?? false,
        useShippingAddressForBilling:
          data.customer.payment_method?.use_shipping_address_for_billing ??
          false,
        additionalData: {
          cartData: query.data ?? null,
          affirmPublicKey,
          shippingInfo: isDigitalOnly
            ? {
                ...splitName(
                  data.customer.payment_method.billing_address?.name ?? "",
                ),
                address1:
                  data.customer.payment_method.billing_address?.address1 ?? "",
                address2:
                  data.customer.payment_method.billing_address?.address2 ?? "",
                city: data.customer.payment_method.billing_address?.city ?? "",
                state:
                  data.customer.payment_method.billing_address?.state ?? "",
                postal_code:
                  data.customer.payment_method.billing_address?.postal_code ??
                  "",
                country:
                  data.customer.payment_method.billing_address?.country ?? "",
                country_code:
                  data.customer.payment_method.billing_address?.country_code ??
                  "",
                email: data.customer.contact_info.email,
              }
            : {
                ...splitName(data.customer.shipping_address.name),
                address1: data.customer.shipping_address.address1,
                address2: data.customer.shipping_address.address2,
                city: data.customer.shipping_address.city,
                state: data.customer.shipping_address.state,
                postal_code: data.customer.shipping_address.postal_code,
                country: data.customer.shipping_address.country,
                country_code: data.customer.shipping_address.country_code,
                email: data.customer.contact_info.email,
              },
        },
      });
    } catch (error) {
      console.error("Checkout error:", error);
    } finally {
      if (!isVgsSubmit) {
        setPaymentLoading(false);
        hideCheckoutLoader();
      }
    }
  };

  const handleInvalidSubmit = (errors: FieldErrors<CheckoutFormData>) => {
    setPaymentLoading(false);
    console.error("Form validation errors:", errors);

    if (errors.customer?.payment_method) {
      const paymentSection = document.querySelector(
        '[aria-label="Credit card form"]',
      );
      if (paymentSection) {
        paymentSection.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
        return;
      }
    }

    const errorFields = [
      { field: errors.customer?.contact_info, ref: contactInfoRef },
      { field: errors.customer?.shipping_address, ref: shippingAddressRef },
    ];

    for (const { field, ref } of errorFields) {
      if (field && ref.current) {
        ref.current.scrollIntoView({ behavior: "smooth" });
        break;
      }
    }
  };

  const handlePayClick = async (e: React.MouseEvent) => {
    e.preventDefault();
    setPaymentLoading(true);

    const paymentMethodType = getValues("customer.payment_method.type");
    const behavior = getPaymentBehavior(paymentMethodType);
    const selectedMethod = query.data?.cart?.available_payment_methods?.find(
      (m) => m.type === paymentMethodType,
    );
    const isVgs =
      behavior === PaymentBehavior.CREDIT_CARD &&
      usesVgsCollect(selectedMethod);

    if (isVgs) {
      // 1. Manually validate everything EXCEPT the Card fields
      let isValid = true;

      const contactValid = await trigger("customer.contact_info");
      if (!contactValid) isValid = false;

      // Validate address and shipping method
      if (isDigitalOnly) {
        const billingValid = await trigger(
          "customer.payment_method.billing_address",
        );
        if (!billingValid) isValid = false;
      } else {
        const shippingValid = await trigger("customer.shipping_address");
        if (!shippingValid) isValid = false;

        const methodValid = await trigger("customer.shipping_method");
        if (!methodValid) isValid = false;
      }

      // Agreements
      if (requiredAgreements.length > 0) {
        const agreementsValid = await trigger("customer.agreements_accepted");
        if (!agreementsValid) isValid = false;
      }

      // Billing Address (if separate)
      const useShipping = getValues(
        "customer.payment_method.use_shipping_address_for_billing",
      );

      if (!useShipping) {
        const billingValid = await trigger(
          "customer.payment_method.billing_address",
        );
        if (!billingValid) isValid = false;
      }

      if (isValid) {
        // Form is valid (ignoring empty card fields), proceed
        await handleValidSubmit(getValues());
      } else {
        // Show errors
        handleInvalidSubmit(checkoutForm.formState.errors);
      }
    } else {
      // Not VGS (PayPal, etc) - use standard validation
      handleSubmit(handleValidSubmit, handleInvalidSubmit)(e);
    }
  };

  const showCheckoutLoaderRef = useRef(showCheckoutLoader);
  const hideCheckoutLoaderRef = useRef(hideCheckoutLoader);
  const checkoutMutationRef = useRef(checkoutMutation);

  useEffect(() => {
    showCheckoutLoaderRef.current = showCheckoutLoader;
    hideCheckoutLoaderRef.current = hideCheckoutLoader;
    checkoutMutationRef.current = checkoutMutation;
  }, [showCheckoutLoader, hideCheckoutLoader, checkoutMutation]);

  const { refetch } = query;

  const handleVgs3DSRedirectSuccess = useCallback(async () => {
    showCheckoutLoaderRef.current("Placing your order...");

    try {
      const checkoutResult = await checkoutMutationRef.current.mutateAsync({
        cartToken,
        shouldIncludeBrowserInfo: false,
        isPostThreeDsRetry: true,
      });

      checkoutRedirect(checkoutResult, cartToken);
    } catch (error) {
      // Deliberately duplicates onError's hideCheckoutLoader: a throw from
      // checkoutRedirect (after a successful mutation) never reaches
      // onError, and this is then the only thing clearing the loader.
      hideCheckoutLoaderRef.current();
      console.error("Checkout after VGS 3DS redirect failed:", error);
      // Error display is handled by checkoutMutation.onError (see Task 8).
      // Refresh cart so UI reflects post-decline state (3DS metadata cleared,
      // cart reverted to payment_method) and the user can retry cleanly.
      refetch();
    }
  }, [cartToken, refetch]);

  useVgs3DSCompletion({
    cartToken,
    onComplete: () => {
      // Close modal, then place the order.
      setVgsThreeDSModal({
        isOpen: false,
        challengeHtml: "",
        challengeUrl: "",
        transactionId: "",
        merchantTxId: "",
      });
      handleVgs3DSRedirectSuccess();
    },
    onError: (error) => {
      setVgsThreeDSModal({
        isOpen: false,
        challengeHtml: "",
        challengeUrl: "",
        transactionId: "",
        merchantTxId: "",
      });
      hideCheckoutLoader();
      setPaymentError(sanitizeErrorMessage(error));
      // Refresh cart state so the UI reflects the backend state (e.g., cart
      // reverted to payment_method) and the user can safely retry.
      refetch();
    },
    enabled: vgsThreeDSModal.isOpen,
    merchantTxId: vgsThreeDSModal.merchantTxId,
    onCanceled: () => {
      setVgsThreeDSModal({
        isOpen: false,
        challengeHtml: "",
        challengeUrl: "",
        transactionId: "",
        merchantTxId: "",
      });
      hideCheckoutLoader();
      setPaymentLoading(false);
      // No error surfaced: a cancel is neutral by design. Refresh so the UI
      // reflects the cart back in payment_method and the user can retry.
      refetch();
    },
  });

  const isDynamicPaymentLoading = paymentMethod
    ? isPaymentLoading(paymentMethod)
    : false;

  const isAnyLoading = paymentLoading || isDynamicPaymentLoading;

  const consolidatedErrorMessage = getConsolidatedErrorMessage(
    t,
    contactErrors,
    errors,
    paymentMethod,
  );

  const getButtonText = () => {
    if (!requiresPaymentMethod) {
      return isAnyLoading
        ? t("processing_payment")
        : t("place_order", "Place order");
    }

    if (query.isLoading) {
      return t("pay_now");
    }

    if (!isAnyLoading) {
      const selectedMethod = query.data?.cart?.available_payment_methods?.find(
        (m) => m.type === paymentMethod,
      );

      if (
        selectedMethod &&
        getPaymentBehavior(paymentMethod) !== PaymentBehavior.CREDIT_CARD &&
        selectedMethod.name !== "Droplet"
      ) {
        return `${t("pay_with")} ${selectedMethod.name}`;
      }

      return t("pay_now");
    }

    if (getPaymentBehavior(paymentMethod) === PaymentBehavior.CREDIT_CARD) {
      const selectedPaymentMethod =
        query.data?.cart?.available_payment_methods?.find(
          (m) => m.type === paymentMethod,
        );

      if (usesVgsCollect(selectedPaymentMethod)) {
        return t("securing_payment");
      }
      return t("processing_payment");
    }

    return t("processing_payment");
  };

  const isPayButtonDisabled =
    isAnyLoading ||
    isSubscriptionPending ||
    isQuantityPending ||
    addressSyncPending ||
    billingAddressSyncPending ||
    updateShippingMethodMutation.isPending ||
    isDiscountPending ||
    isRemoveDiscountPending ||
    hasCartErrors(query.data?.cart ?? null) ||
    hasCartItemErrors(query.data?.cart ?? null) ||
    hasCreditCardErrors(paymentMethod, errors);

  return (
    <section
      ref={scrollRef}
      aria-labelledby="payment-heading"
      className="relative col-span-4 flex-auto bg-white px-8 pt-0 pb-16 lg:h-[94vh] lg:overflow-y-auto"
    >
      <div
        className={cn("mx-auto max-w-lg lg:mx-0 lg:mr-auto", {
          "pointer-events-none opacity-60 select-none": isAnyLoading,
        })}
        aria-busy={isAnyLoading}
      >
        {query.data?.cart?.points_enabled &&
          (query.data?.cart?.total_creditable_points ?? 0) > 0 && (
            <InfoAlert
              color={
                query.data.cart.company?.checkout_primary_button_color ??
                undefined
              }
              icon={<span>🎉</span>}
              className="my-6"
            >
              <p className="text-sm font-medium">
                {t("earning_points_on_order", undefined, {
                  points: query.data.cart.total_creditable_points ?? 0,
                  label: pointsLabel(
                    query.data.cart.total_creditable_points ?? 0,
                    query.data.cart.company,
                  ),
                })}
              </p>
              <button
                type="button"
                onClick={() => router.push(`/checkouts/${cartToken}/login`)}
                className="text-sm underline hover:opacity-80"
              >
                {t("sign_in_to_redeem_rewards")}
              </button>
            </InfoAlert>
          )}

        <FastCheckoutOptions
          cartToken={cartToken}
          isLoading={query.isLoading}
          paymentMethodsIncludes={paymentMethodsIncludes}
          subdomain={subdomain ?? null}
          query={query}
          kountSessionId={kountSessionId}
        />
        <ContactInfoForm
          query={query}
          control={control}
          cartToken={cartToken}
          ref={contactInfoRef as RefObject<HTMLDivElement>}
          contactErrors={contactErrors}
          setContactErrors={setContactErrors}
          fluidShop={query.data?.cart?.company?.subdomain ?? ""}
          formEmail={formEmail}
          collectPhone={query.data?.cart?.company?.collect_phone ?? false}
          paymentMethodsIncludes={paymentMethodsIncludes ?? null}
          requiredPhone={query.data?.cart?.company?.require_phone ?? false}
          setValue={setValue}
          clearErrors={clearErrors}
        />
        <DropZone
          zone="below_contact"
          token={cartToken}
          subdomain={subdomain ?? null}
          page="checkout"
          companyId={query.data?.cart?.company?.id}
        />
        <div className="mt-4">
          <AddressForm
            control={control}
            countries={countries}
            watch={watch}
            fieldPath={fieldPath}
            stateOptions={stateOptions}
            addressFields={shippingAddressFieldsConfig}
            onCountryChange={onShippingCountryChange}
            renderAutocompleteOptions={renderAutocompleteOptions}
            getAutocompleteOptions={getAutocompleteOptions}
            handleOptionClick={handleOptionClick}
            ref={shippingAddressRef as RefObject<HTMLDivElement>}
            networkError={addressError}
            title={isDigitalOnly ? t("billing_address") : t("shipping_address")}
            isLoading={query.isLoading}
            clearErrors={clearErrors}
          />
          <DropZone
            zone="below_shipping_address"
            token={cartToken}
            subdomain={subdomain ?? null}
            page="checkout"
            companyId={query.data?.cart?.company?.id}
          />
        </div>
        {!query?.data?.cart.digital_only && (
          <ShippingMethodSelect
            control={control}
            isLoading={
              query.isLoading || addressSyncPending || isQuantityPending
            }
            onSelect={onSelectShippingMethod}
            options={query.data?.cart.available_shipping_methods || []}
            networkError={shippingMethodError}
            setNetworkError={setShippingMethodError}
            addressId={query.data?.cart.ship_to?.id}
            selectedShippingMethod={query.data?.cart.shipping_method}
            isShippingMethodMutationPending={
              updateShippingMethodMutation.isPending
            }
          />
        )}
        <DropZone
          zone="below_shipping_method"
          token={cartToken}
          subdomain={subdomain ?? null}
          page="checkout"
          companyId={query.data?.cart?.company?.id}
        />
        {requiresPaymentMethod && !paymentMethodAutoAssignable && (
          <PaymentMethodSelect
            control={control}
            query={query}
            setValue={setValue}
            watch={watch}
            setError={setError}
            clearErrors={clearErrors}
            networkError={paymentError}
            countries={countries}
            cartToken={cartToken}
            paymentMethodsIncludes={paymentMethodsIncludes}
            disabled={isPayButtonDisabled}
            isFormReady={isFormReady}
            iframeLoadError={iframeLoadError}
            hasActiveVgsSession={hasActiveSession}
            vgsFieldState={vgsFieldState}
            resetVgsForm={resetVgs}
            onCustomerDetailsChange={setCustomerDetails}
            customerDetailsValidationErrors={customerDetailsValidationErrors}
            onClearCustomerDetailsFieldError={(fieldKey) => {
              setCustomerDetailsValidationErrors((prev) => {
                const newErrors = { ...prev };
                delete newErrors[fieldKey];
                return newErrors;
              });
            }}
          />
        )}
        <DropZone
          zone="below_payment_method"
          token={cartToken}
          subdomain={subdomain ?? null}
          page="checkout"
          companyId={query.data?.cart?.company?.id}
        />
        <div className="mt-6">
          {optionalCheckboxAgreements.length > 0 && (
            <OptionalAgreements
              agreements={optionalCheckboxAgreements}
              onCheckedChange={setOptionalCheckedIds}
            />
          )}
          <MarketingCheckboxes
            collectEmailMarketing={
              query.data?.cart?.company?.collect_email_marketing ?? false
            }
            collectSmsMarketing={
              query.data?.cart?.company?.collect_sms_marketing ?? false
            }
            setEmailMarketing={setEmailMarketing}
            setSmsMarketing={setSmsMarketing}
          />
          {requiredAgreements.length > 0 && (
            <Agreements
              control={control}
              agreements={requiredAgreements}
              cartToken={cartToken}
              subdomain={subdomain ?? null}
              companyId={query.data?.cart?.company?.id}
            />
          )}
        </div>
        {checkoutError && (
          <ErrorAlert
            message={checkoutError}
            testId="checkout-error"
            className="mb-4"
          />
        )}
        {hasCartErrors(query.data?.cart ?? null) && (
          <ErrorAlert
            message={(query.data?.cart?.messages?.errors || []).join(",")}
            className="mb-4"
          />
        )}
        {(query.data?.cart?.messages?.warnings ?? []).map((message, index) => (
          <WarningAlert
            key={`cart-warning-${index}`}
            message={message}
            className="my-4"
          />
        ))}
        {(query.data?.cart?.messages?.info ?? []).map((message, index) => (
          <MutedAlert
            key={`cart-info-${index}`}
            message={message}
            className="my-4"
          />
        ))}
        {hasCartItemErrors(query.data?.cart ?? null) && (
          <ErrorAlert message={t("fix_cart_items_message")} className="mb-4" />
        )}

        {consolidatedErrorMessage && (
          <ErrorAlert message={consolidatedErrorMessage} className="mb-4" />
        )}
        {paymentMethod === "paypal" && sdkType === "braintree" ? (
          <div className="space-y-3">
            {braintreePaypalBtn}
            {braintreeVenmoBtn}
            {braintreePayLaterBtn}
            {braintreePayLaterMsg}
          </div>
        ) : paymentMethod === "paypal" && (paypalButton || venmoButton) ? (
          <div className="space-y-3">
            {paypalButton}
            {venmoButton}
            {payLaterButton}
          </div>
        ) : query.isLoading ? null : (
          <Button
            className="h-input bg-brand-primary text-brand-primary-text mt-6 flex w-full items-center justify-center rounded px-6 py-2 text-base font-semibold opacity-95 hover:opacity-100"
            onClick={handlePayClick}
            disabled={isPayButtonDisabled || iframeLoadError}
            data-testid="submit-order"
          >
            {isAnyLoading && (
              <div className="mr-3 h-5 w-5 animate-spin rounded-full border-4 border-t-4 border-white border-t-transparent"></div>
            )}
            {getButtonText()}
          </Button>
        )}

        {footerAgreements.length > 0 && (
          <Footer
            isLoading={query.isLoading}
            companyName={query.data?.cart?.company?.name ?? ""}
            agreements={footerAgreements}
            className="mt-8 py-4"
          />
        )}
      </div>

      {/* VGS 3DS Modal */}
      <VgsThreeDSModal
        isOpen={vgsThreeDSModal.isOpen}
        challengeHtml={vgsThreeDSModal.challengeHtml}
        challengeUrl={vgsThreeDSModal.challengeUrl}
        transactionId={vgsThreeDSModal.transactionId}
        cartToken={cartToken}
        onClose={(error?: string) => {
          const { merchantTxId } = vgsThreeDSModal;
          setVgsThreeDSModal({
            isOpen: false,
            challengeHtml: "",
            challengeUrl: "",
            transactionId: "",
            merchantTxId: "",
          });
          if (error) {
            setPaymentLoading(false);
            setPaymentError(error);
            return;
          }
          // Customer abandoned the challenge: cancel the attempt server-side
          // so the cart leaves three_ds_pending now instead of at the T+6
          // safety net (kills the retry race - close used to be local-only).
          // Any failure falls back to today's local-only close so the
          // customer is never stranded. Neutral by contract: the backend
          // writes CANCELED, never three_ds_failed_at or an error message.
          if (!merchantTxId) {
            setPaymentLoading(false);
            return;
          }
          // Hold Pay disabled while the cancel is in flight (bounded by the
          // call's 5s abort) so an instant re-Pay can't race the still-
          // pending cart into the backend's active-challenge guard.
          setPaymentLoading(true);
          void cancelVgs3DS({ cartToken, merchantTxId })
            .catch((error: unknown) => {
              // Observability only — the local-only close is the designed
              // fallback and the safety net cleans the attempt at T+6.
              console.warn(
                "[VGS 3DS] cancel request failed; falling back to local-only close",
                error,
              );
            })
            .finally(() => {
              setPaymentLoading(false);
              // Refresh so the UI reflects the cart back in payment_method
              // (mirrors the onError/onCanceled handlers).
              refetch();
            });
        }}
      />

      {/* BLIK OTP Modal */}
      <BlikOtpModal
        isOpen={blikOtpModal.isOpen}
        cartToken={cartToken}
        onClose={(error?: string) => {
          setBlikOtpModal({ isOpen: false });
          setPaymentLoading(false);
          if (error) {
            setPaymentError(error);
          }
        }}
      />
    </section>
  );
}
